import { motion } from 'framer-motion';
import { ArrowLeft, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-16">
      <Helmet>
        <title>Terms of Service | VortexWebCo</title>
        <meta name="description" content="Terms of Service for Vortex Web Co - Usage terms for vortexweb.co website and services." />
        <meta property="og:title" content="Terms of Service | VortexWebCo" />
        <meta property="og:description" content="Terms of Service for Vortex Web Co - Usage terms for vortexweb.co website and services." />
        <meta property="og:url" content="https://vortexweb.co/terms" />
        <meta property="og:site_name" content="vortexweb.co" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#e62020] transition-colors font-mono text-sm"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Scale size={20} className="text-[#e62020]" />
            <span className="text-[#e62020] font-mono text-sm tracking-[0.3em] uppercase">
              // LEGAL
            </span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
            data-testid="terms-title"
          >
            Terms of <span className="text-[#e62020]">Service</span>
          </h1>
          <p className="text-gray-500 font-mono text-sm">
            Vortex Web Co | vortexweb.co | Effective upon use
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Agreement */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">01</span>
              Agreement to Terms
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Use of <strong className="text-white">vortexweb.co</strong> constitutes agreement to these terms. By accessing or using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>

          {/* Payments */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">02</span>
              Payments
            </h2>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Payments are processed securely via <strong className="text-white">Stripe</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>All payments are <strong className="text-white">non-refundable</strong></span>
              </li>
            </ul>
          </section>

          {/* Liability */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">03</span>
              Limitation of Liability
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Vortex Web Co is <strong className="text-white">not liable for indirect damages</strong>. This includes, but is not limited to, loss of profits, data, business opportunities, or any consequential damages arising from the use of our services.
            </p>
          </section>

          {/* Service Disclaimer */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">04</span>
              Service Disclaimer
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Services are provided <strong className="text-white">"as-is"</strong> without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>
          </section>

          {/* Termination */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">05</span>
              Termination
            </h2>
            <p className="text-gray-400 leading-relaxed">
              We reserve the right to <strong className="text-white">terminate service for non-payment</strong> or violation of these terms. Upon termination, your right to use our services will immediately cease.
            </p>
          </section>

          {/* Governing Law */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">06</span>
              Governing Law
            </h2>
            <p className="text-gray-400 leading-relaxed">
              These terms are governed by the laws of <strong className="text-white">Texas, United States</strong>. Any disputes shall be resolved in the courts of Texas.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-[#0d0d0d] border border-[#e62020]/20 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">07</span>
              Contact
            </h2>
            <p className="text-gray-400 mb-4">
              For questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 font-mono text-sm">
              <p className="text-white mb-1">Vortex Web Co</p>
              <p className="text-gray-500">Website: <a href="https://vortexweb.co" className="text-[#e62020] hover:underline">vortexweb.co</a></p>
              <p className="text-gray-500">Email: <a href="mailto:vortexwebco@gmail.com" className="text-[#e62020] hover:underline">vortexwebco@gmail.com</a></p>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};
